<script setup lang="ts">
/**
 * The visitor's past conversations — P2-52.
 *
 * WHY THIS SCREEN EXISTS AT ALL. An anonymous lead who came back a week later
 * used to see one thread and no way to reach what they wrote last time, even
 * though the continuity credential had already resumed the same person and the
 * rows were sitting there under the visitor policy. The list is not new data; it
 * is data that was already theirs and unreachable.
 *
 * EVERY ROW IS A `<button>` INSIDE A `<li>`, and that is not decoration. A
 * clickable `<div>` is invisible to a screen reader's element list, unreachable
 * by Tab, and deaf to Space — so a visitor using a keyboard on a phone's
 * external keyboard, or a screen reader on a page they are trying to get help
 * with, would be shown a history they cannot open.
 */
import { computed, useTemplateRef, watchEffect } from 'vue';

import type { WidgetView } from '../app/useWidget';
import { useVisitorFormat } from '../i18n/useVisitorFormat';
import { previewLine, statusLabelId } from '../model/history';
import { useText } from '../vendor/i18n';
import { OwButton, OwSpinner } from '../vendor/ui';

import UnavailableNotice from './UnavailableNotice.vue';

const props = defineProps<{ readonly view: WidgetView }>();

const text = useText();
const format = useVisitorFormat();
const history = computed(() => props.view.history);
const currentId = computed(() => props.view.conversation?.id ?? null);

/**
 * INFINITE SCROLL RATHER THAN A "LOAD MORE" BUTTON, and the reason is copy
 * rather than taste: a button needs a label, this task ships fourteen strings
 * in fifty languages, and none of them says "load more". A sentinel needs no
 * label at all — reaching the bottom of a list IS the request.
 *
 * The observer is torn down and rebuilt whenever `loading` or `hasMore`
 * changes, so a page that is already in flight cannot be asked for twice.
 */
const sentinel = useTemplateRef<HTMLDivElement>('sentinel');
watchEffect(
  (onCleanup) => {
    const node = sentinel.value;
    const { hasMore, loading, loadMore } = history.value;
    if (node === null || !hasMore || loading) {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadMore();
      }
    });
    observer.observe(node);
    onCleanup(() => {
      observer.disconnect();
    });
  },
  { flush: 'post' },
);

function previewOf(preview: string | null): string {
  const line = previewLine(preview);
  return line.kind === 'preview' ? line.text : text('widget.history.no_messages');
}

function choose(id: string): void {
  // The row the visitor is already in is not a past conversation, so opening
  // it read-only would take away the composer they were using. It goes back to
  // the live chat.
  if (id === currentId.value) {
    props.view.backToChat();
  } else {
    props.view.openPast(id);
  }
}
</script>

<template>
  <div v-if="history.loading && history.items.length === 0" class="ow-centred">
    <OwSpinner :label="text('widget.conversation.loading')" />
  </div>

  <!--
    A failure with rows already on screen is a failed NEXT page, and the rows
    stay: replacing a list the visitor is reading with an error, because the
    page below it did not arrive, loses more than it explains.
  -->
  <UnavailableNotice
    v-else-if="history.failed && history.items.length === 0"
    :detail="text('widget.error.unreachable')"
  >
    <OwButton variant="secondary" @click="history.loadMore()">
      {{ text('widget.error.retry') }}
    </OwButton>
  </UnavailableNotice>

  <div v-else-if="history.items.length === 0" class="ow-centred">
    <p class="ow-history__empty">{{ text('widget.history.empty') }}</p>
  </div>

  <div v-else class="ow-history">
    <ul class="ow-history__list" role="list">
      <li v-for="summary in history.items" :key="summary.id">
        <button type="button" class="ow-history__row" @click="choose(summary.id)">
          <span class="ow-history__meta">
            <time :datetime="summary.lastActivityAt">
              {{ format.timestamp(summary.lastActivityAt) }}
            </time>
            <span class="ow-history__status">
              {{ text(statusLabelId(summary.statusCategory)) }}
            </span>
            <span v-if="summary.id === currentId" class="ow-history__badge">
              {{ text('widget.history.current') }}
            </span>
          </span>
          <span class="ow-history__preview">
            {{ previewOf(summary.preview) }}
          </span>
        </button>
      </li>
    </ul>
    <div v-if="history.hasMore" ref="sentinel" aria-hidden="true" />
  </div>
</template>
